import React, { FC, useState, useCallback, useEffect } from 'react'
import cn from 'classnames'
import { ListChildComponentProps } from 'react-window'
import ClickOutside from 'react-click-outside'
import ProductPhoto from '../../components/ProductPhoto'
import BinIcon from '../../icons/bin.svg'
import { IOrderItem } from '../../types/orders'
import { money } from '../../common/display'
import StockState from './StockState'

const OrderRow: FC<ListChildComponentProps> = ({ data, index, style }) => {
  const [focused, setFocused] = useState<boolean>(false)
  const orderItem: IOrderItem = data.orderItem || data.orderItems[index]

  const draft = data.drafts[orderItem.id]
  const value = draft != null ? draft : orderItem.amountRequested
  const total = money(+(value || 0) * +(orderItem.effectivePrice || 0))
  const price = `${money(orderItem.effectivePrice || 0)} / ${
    orderItem.effectiveUnit || ''
  }`
  const isUnavailable = orderItem.product.unavailable
  const canDelete = data.editable
  const isEditable = data.editable && !isUnavailable
  const multiCatalogEnabled = data.multiCatalogEnabled
  const isLastUnavailable =
    data.unavailableCount > 0 && index === data.unavailableCount - 1
  const handleDelete = useCallback(() => {
    if (!confirm('Are you sure you want to remove the item from order?')) return
    data.onDelete(orderItem)
  }, [data.onDelete, orderItem])

  useEffect(() => {
    if (data.autoEditIndex !== index) return
    data.onAutoEditHandled?.()
    if (isEditable) setFocused(true)
  }, [data.autoEditIndex, index, isEditable])

  const showPalletQuantity = (orderItem.product.palletQuantity ?? 0) > 0
  const showCaseQuantity = (orderItem.product.caseQuantity ?? 0) > 0

  return (
    <div
      className={cn('order-modal_list-item', {
        '-last-unavailable': isLastUnavailable,
      })}
      style={style}
    >
      <div className="order-modal_list-product order-snippet -small">
        <div className="order-snippet_col -info -large">
          <div className="order-snippet_image-wrapper">
            <ProductPhoto
              photo={orderItem.product.photo}
              className="order-snippet_image"
            />
          </div>
          <div
            className={cn('order-snippet_title', {
              '-unavailable': isUnavailable,
            })}
          >
            {orderItem.product.name}
          </div>
          <div className="order-snippet_info">
            <div className="order-snippet_sku">{orderItem.product.code}</div>
            {multiCatalogEnabled && (
              <div className="order-snippet_erp-catalog">
                {orderItem.product.erpCatalogName}
              </div>
            )}
          </div>
          <div className="order-snippet_price">{price}</div>
          {(showPalletQuantity || showCaseQuantity) && (
            <div className="order-snippet_packaging">
              {[
                showPalletQuantity &&
                  `${orderItem.product.palletQuantity} / PALLET`,
                showCaseQuantity && `${orderItem.product.caseQuantity} / CASE`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          )}
        </div>
        {canDelete && (
          <div className="order-snippet_action" onClick={handleDelete}>
            <BinIcon />
          </div>
        )}
      </div>
      <div
        className={cn('tile -bordered', {
          'clear-button': isEditable,
          '-done': !isEditable,
        })}
        onClick={() => !focused && isEditable && setFocused(true)}
      >
        {focused ? (
          <ClickOutside
            onClickOutside={() => {
              setFocused(false)
              data.onAmountFlush(orderItem)
            }}
            className="tile_count"
          >
            <input
              type="number"
              value={value.toString()}
              onChange={(e) => data.onAmountChange(orderItem, e.target.value)}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== 'Tab') return
                data.onAmountFlush(orderItem)
                if (e.key === 'Tab') {
                  e.preventDefault()
                  setFocused(false)
                  data.onFocusNextRow?.(index)
                }
              }}
              className="tile_count-input"
              autoFocus
            />
          </ClickOutside>
        ) : (
          <button
            className="tile_count-toggle clear-button"
            onKeyDown={(e) => {
              if (e.key === 'Tab' && !e.shiftKey) {
                e.preventDefault()
                data.onFocusNextRow?.(index)
              }
            }}
          >
            {value}
          </button>
        )}
        {isEditable &&
          orderItem.product.stockState &&
          orderItem.product.stockInventory !== undefined && (
            <StockState
              state={orderItem.product.stockState}
              inventory={orderItem.product.stockInventory}
            />
          )}
      </div>
      <div className="tile -done -bordered">{total}</div>
    </div>
  )
}

export default OrderRow
